import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';

import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { IAhsSelectModel } from '@nx-ahs/base-ahs-controls/models';
import { AhsSelectComponent, AhsTextComponent, AhsTextareaComponent } from '@nx-ahs/base-ahs-controls/reactive';

import { ClientTranslationCreationData, ClientTranslationKeyModel, LocaleModel } from '../models/language-locale.model';
import {
	DefaultTranslationRecordValues,
	TranslationKeyCreationRefData
} from '../models/translation-key-creation-ref-data';

import { TranslationKeyCreationService } from '../services/translation-key-creation/translation-key-creation.service';

@Component({
	selector: 'client-translation-create-new-record',
	templateUrl: './client-translation-create-new-record.component.html',
	styleUrls: ['./client-translation-create-new-record.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatCheckboxModule,
		MatButtonModule,
		MatDialogModule,
		FlexLayoutModule,
		AhsSelectComponent,
		AhsTextComponent,
		AhsTextareaComponent
	]
})
export class ClientTranslationCreateNewRecordComponent implements OnInit {
	public clientTypeOptions: IAhsSelectModel[] = [];
	public languageCodeOptions: IAhsSelectModel[] = [];
	public portalPageNameOptions: IAhsSelectModel[] = [];
	public createNewTranslationRecordForm: FormGroup = null;
	public translationKeyCreationRefData: TranslationKeyCreationRefData = null;

	constructor(
		private translationKeyCreationService: TranslationKeyCreationService,
		private formBuilder: FormBuilder,
		private dialogRef: MatDialogRef<ClientTranslationCreateNewRecordComponent>
	) {
		this.buildCreateNewTranslationRecordForm();
	}

	async ngOnInit(): Promise<void> {
		await this.getTranslationKeyCreationRefData();
		this.buildDropDownModels();
	}

	public async submitCreateRecordRequest(): Promise<void> {
		if (this.createNewTranslationRecordForm.valid) {
			await this.translationKeyCreationService
				.createTranslationKeyRecord(this.getValuesFormTranslationKeyForm())
				.then((createdRecords) => {
					alert(JSON.stringify(createdRecords, null, 2).toString());
				})
				.catch((error) => {
					console.log(JSON.stringify(error));
					alert(error.status + ': ' + JSON.stringify(error.error));
				});
		}
	}

	public closeTranslationCreationModal(): void {
		this.dialogRef.close();
	}

	public async getTranslationKeyCreationRefData(): Promise<void> {
		this.translationKeyCreationRefData = await this.translationKeyCreationService
			.getTranslationKeyCreationRefData()
			.toPromise();
	}

	private getValuesFormTranslationKeyForm(): ClientTranslationCreationData {
		// Extract the raw value from the portalPageName form control
		const portalPageNameValue = this.newTranslationKeyFormControls['portalPageName'].value;

		// Ensure portalPageName is a string[]:
		// - If the value is truthy and already an array, use it as is
		// - If the value is truthy but not an array (e.g., a single string), wrap it in an array
		// - If the value is falsy (null, undefined, etc.), use an empty array
		const portalPageName = portalPageNameValue
			? Array.isArray(portalPageNameValue)
				? portalPageNameValue
				: [portalPageNameValue]
			: [];

		// For completeness, handle other fields
		const languageCodeValue = this.newTranslationKeyFormControls['languageCode'].value;
		const languageCode = Array.isArray(languageCodeValue) ? languageCodeValue[0] : languageCodeValue;

		const clientTypeIdValue = this.newTranslationKeyFormControls['clientType'].value;
		const clientTypeId = Array.isArray(clientTypeIdValue) ? clientTypeIdValue[0] : clientTypeIdValue;

		// Construct the locale model (languageCode as string, translatedText as string)
		const localeModel: LocaleModel = {
			languageCode: languageCode,
			translatedText: this.newTranslationKeyFormControls['translatedText'].value
		};

		// Construct the form data (portalPageName as string[])
		const formData: ClientTranslationKeyModel = {
			key: this.newTranslationKeyFormControls['translationKey'].value,
			languages: [localeModel], // Array of locale objects
			portalPageName: portalPageName, // Now guaranteed to be string[]
			clientTypeId: clientTypeId,
			clientTypeName: null
		};

		// Construct the final data object
		const newKeyCreationData: ClientTranslationCreationData = {
			clientKeyData: formData,
			createRecordsForAllLanguages: this.newTranslationKeyFormControls['createRecordsForAllLanguages'].value
		};

		return newKeyCreationData;
	}

	/**
	 * TODO - Set defaults on these dropdowns
	 * value: DefaultTranslationRecordValues.ClientType_HealthTrackAPI
	 * value: DefaultTranslationRecordValues.LanguageCode_English
	 */
	private buildCreateNewTranslationRecordForm(): void {
		this.createNewTranslationRecordForm = this.formBuilder.group({
			clientType: [null, Validators.required],
			portalPageName: [null],
			languageCode: [null, Validators.required],
			translationKey: [null, [Validators.required]],
			translatedText: [null, [Validators.required]],
			createRecordsForAllLanguages: [false, [Validators.required]]
		});
	}

	private buildClientTypeOptions(): void {
		this.translationKeyCreationRefData.clientTypes.forEach((clientType) => {
			const selectModel = new IAhsSelectModel();
			selectModel.id = clientType.id;
			selectModel.viewValue = clientType.name;
			selectModel.value = clientType.id;

			this.clientTypeOptions.push(selectModel);
		});
	}

	private buildLanguageCodeOptions(): void {
		this.translationKeyCreationRefData.languageCodes.forEach((langCode) => {
			const selectModel = new IAhsSelectModel();
			selectModel.id = langCode.id;
			selectModel.viewValue = langCode.name;
			selectModel.value = langCode.name;

			this.languageCodeOptions.push(selectModel);
		});
	}

	private buildPortalPageNameOptions(): void {
		this.translationKeyCreationRefData.portalPageNames.forEach((portalPageName, index) => {
			const selectModel = new IAhsSelectModel();
			selectModel.id = index;
			selectModel.viewValue = portalPageName.pageName;
			selectModel.value = portalPageName.pageName;

			this.portalPageNameOptions.push(selectModel);
		});
	}

	private buildDropDownModels(): void {
		this.buildClientTypeOptions();
		this.buildLanguageCodeOptions();
		this.buildPortalPageNameOptions();
	}

	private get newTranslationKeyFormControls() {
		return this.createNewTranslationRecordForm.controls;
	}
}
